'use client'

import React from 'react'
import styled from 'styled-components'

import { ProductCatalogVariantGroup } from '../../../__generated__/graphql/catalog/graphql'
import { formatPrettyCurrency } from '../../../lib/utils/currency'
import { useLocalization } from '../../_providers/LocalizationProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'

import { CompareAtPrice } from './CompareAtPrice.client'
import { MarkedDownPrice } from './MarkedDownPrice.client'

export type PricingInformation = {
  priceRange: { min: number; max: number }
  discountPriceRange?: {
    min: number
    max: number
    minPercentageOff: number | null
    maxPercentageOff: number | null
  }
  currencyCode: string
  unit: 'cents' | 'dollars'
}

export function derivePricingForVariant(variant: {
  compareAtPrice?: {
    amount: NonNullable<
      NonNullable<ProductCatalogVariantGroup['variant']>['compareAtPrice']
    >['amount']
  } | null
  price: {
    amount: NonNullable<ProductCatalogVariantGroup['variant']>['price']['amount']
    currencyCode: NonNullable<ProductCatalogVariantGroup['variant']>['price']['currencyCode']
  }
}): PricingInformation | undefined {
  const minPrice = variant.compareAtPrice?.amount ?? variant.price.amount
  const maxPrice = variant.compareAtPrice?.amount ?? variant.price.amount
  const minDiscountPrice = variant.price.amount
  const maxDiscountPrice = variant.price.amount

  const discountPriceRange =
    minDiscountPrice < minPrice
      ? {
          min: minDiscountPrice,
          max: maxDiscountPrice,
          maxPercentageOff: null,
          minPercentageOff: null,
        }
      : undefined

  return {
    priceRange: {
      min: minPrice,
      max: maxPrice,
    },
    discountPriceRange: discountPriceRange,
    currencyCode: variant.price.currencyCode,
    unit: 'dollars',
  }
}

export function derivePricingForVariantGroup(variantGroup: {
  compareAtPriceRange?: {
    min: {
      amount: number
    }
    max: {
      amount: number
    }
    minPercentageOff: number
    maxPercentageOff: number
  } | null
  priceRange: {
    currencyCode: string
    min: {
      amount: number
    }
    max: {
      amount: number
    }
  }
}): PricingInformation {
  const minPrice =
    variantGroup.compareAtPriceRange?.min.amount ?? variantGroup.priceRange.min.amount
  const maxPrice =
    variantGroup.compareAtPriceRange?.max.amount ?? variantGroup.priceRange.max.amount
  const minDiscountPrice = variantGroup.priceRange.min.amount
  const maxDiscountPrice = variantGroup.priceRange.max.amount

  const discountPriceRange =
    minDiscountPrice < minPrice
      ? {
          min: minDiscountPrice,
          max: maxDiscountPrice,
          maxPercentageOff: variantGroup.compareAtPriceRange?.maxPercentageOff
            ? Math.round(variantGroup.compareAtPriceRange.maxPercentageOff * 100)
            : null,
          minPercentageOff: variantGroup.compareAtPriceRange?.minPercentageOff
            ? Math.round(variantGroup.compareAtPriceRange.minPercentageOff * 100)
            : null,
        }
      : undefined

  return {
    priceRange: {
      min: minPrice,
      max: maxPrice,
    },
    discountPriceRange: discountPriceRange,
    currencyCode: variantGroup.priceRange.currencyCode,
    unit: 'dollars',
  }
}

type ProductGroupWithPricedProducts = {
  products: Array<{
    priceRange:
      | null
      | undefined
      | {
          min: number
          max: number
        }
    defaultVariant: {
      price: number
      discountPrice: null | undefined | number
    }
  }>
}

export function derivePricingForProductGroup(
  productGroupWithPricedProducts: ProductGroupWithPricedProducts,
  currencyCode: string
): PricingInformation | undefined {
  let pricingInformation: PricingInformation | undefined = undefined

  for (const {
    defaultVariant: { price, discountPrice },
    priceRange,
  } of productGroupWithPricedProducts.products) {
    const minPrice = Math.min(
      pricingInformation?.priceRange.min ?? price,
      priceRange?.min ?? price,
      price
    )
    const maxPrice = Math.max(
      pricingInformation?.priceRange.max ?? price,
      priceRange?.max ?? price,
      price
    )

    const percentageOff: number | undefined = discountPrice
      ? Math.round(((price - discountPrice) / price) * 100)
      : undefined
    const minDiscountPrice = Math.min(
      pricingInformation?.discountPriceRange?.min ?? discountPrice ?? price,
      discountPrice ?? price
    )
    const maxDiscountPrice = Math.max(
      pricingInformation?.discountPriceRange?.max ?? discountPrice ?? price,
      discountPrice ?? price
    )
    const maxPercentageOff = Math.max(
      pricingInformation?.discountPriceRange?.maxPercentageOff ?? 0,
      percentageOff ?? 0
    )
    const minPercentageOff = Math.min(
      percentageOff ?? maxPercentageOff,
      pricingInformation?.discountPriceRange?.minPercentageOff ?? maxPercentageOff
    )

    const discountPriceRange =
      minDiscountPrice < minPrice
        ? {
            min: minDiscountPrice,
            max: maxDiscountPrice,
            maxPercentageOff,
            minPercentageOff,
          }
        : undefined

    pricingInformation = {
      priceRange: {
        min: minPrice,
        max: maxPrice,
      },
      discountPriceRange: discountPriceRange,
      currencyCode,
      unit: 'cents',
    }
  }

  return pricingInformation
}

type PricingRangeProps = PricingInformation & {
  renderPriceText?: (children: React.ReactNode) => React.ReactNode
  renderMarkdownText?: (children: React.ReactNode) => React.ReactNode
  renderCompareAtText?: (children: React.ReactNode) => React.ReactNode
  className?: string
  enablePercentageOffText?: boolean
}

export const PricingRange = styled(
  ({
    unit,
    currencyCode,
    priceRange,
    discountPriceRange,
    renderPriceText,
    renderMarkdownText,
    renderCompareAtText,
    enablePercentageOffText,
    className,
  }: PricingRangeProps) => {
    const { locale } = useLocalization()
    const {
      magnolia: {
        priceInfo: { percentageOff, upToPercentageOff },
      },
    } = useTranslation()

    const percentageOffStatement =
      enablePercentageOffText && discountPriceRange
        ? discountPriceRange.minPercentageOff !== discountPriceRange.maxPercentageOff
          ? ` (${upToPercentageOff.replace(
              '{{percentage}}',
              discountPriceRange.maxPercentageOff + ''
            )})`
          : ` (${percentageOff.replace(
              '{{percentage}}',
              discountPriceRange.maxPercentageOff + ''
            )})`
        : ''

    const discountRangeStatement = discountPriceRange
      ? discountPriceRange.min !== discountPriceRange.max
        ? `${formatPrettyCurrency({
            value: discountPriceRange.min,
            unit,
            currency: currencyCode,
            locale,
          })}-${formatPrettyCurrency({
            value: discountPriceRange.max,
            unit,
            currency: currencyCode,
            locale,
          })}${percentageOffStatement}`
        : `${formatPrettyCurrency({
            value: discountPriceRange.min,
            unit,
            currency: currencyCode,
            locale,
          })}${percentageOffStatement}`
      : undefined

    const priceRangeStatement =
      priceRange.min !== priceRange.max
        ? `${formatPrettyCurrency({
            value: priceRange.min,
            unit,
            currency: currencyCode,
            locale,
          })}-${formatPrettyCurrency({
            value: priceRange.max,
            unit,
            currency: currencyCode,
            locale,
          })}`
        : `${formatPrettyCurrency({
            value: priceRange.min,
            unit,
            currency: currencyCode,
            locale,
          })}`

    return (
      <>
        {discountRangeStatement ? (
          <span className={className}>
            {renderMarkdownText ? (
              renderMarkdownText(discountRangeStatement)
            ) : (
              <MarkedDownPrice>{discountRangeStatement}</MarkedDownPrice>
            )}
            {renderCompareAtText ? (
              renderCompareAtText(priceRangeStatement)
            ) : (
              <CompareAtPrice>{priceRangeStatement}</CompareAtPrice>
            )}
          </span>
        ) : (
          <span className={className}>
            {renderPriceText ? renderPriceText(priceRangeStatement) : priceRangeStatement}
          </span>
        )}
      </>
    )
  }
)`
  display: inline-flex;
  column-gap: 0.6ch;
  flex-wrap: wrap;

  > * {
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: auto;
  }
`
